import { Component } from 'preact';

/**
 * CandlePlacementDev. Dev-only visual editor for scene candle positions.
 *
 * Render this INSIDE a CoverArtCanvas (or any container whose layout
 * box matches the candle coordinate system) so click/drag positions
 * map back to the same percentages the production CandleFlames uses.
 * The catcher and handles use absolute positioning to inherit that
 * containing block; the toggle pill and side panel stay fixed so
 * they always sit at the viewport corner.
 *
 * Workflow:
 *   1. Click the "Edit candles" pill (top-right) to enter edit mode.
 *   2. Drag a handle to move an existing candle.
 *   3. Click on empty background to add a new candle at that point.
 *   4. Right-click a handle to delete.
 *   5. Tweak size/glow/speed/phase in the panel on the right.
 *   6. Hit "Copy JS." A `DEFAULT_CANDLES = [...]` snippet lands in your
 *      clipboard, ready to paste into the scene's candle constant.
 *
 * State is persisted to localStorage so reloads don't wipe your edits.
 * Rendering of the actual flames is owned by the parent. This component
 * just mutates the candle array and hands it back via props.onChange.
 */

// localStorage is a working buffer for in-progress dev edits. The
// source of truth is DEFAULT_CANDLES in each scene. Once you're happy,
// hit "Copy JS" and paste into the scene file. Reloads restore WIP
// from localStorage so you don't lose placements between browser refreshes.
function candlesKey(sceneKey) { return `valkenhall:dev:candles:${sceneKey}`; }
function panelPosKey(sceneKey) { return `valkenhall:dev:candles:${sceneKey}:panel`; }

function loadStoredCandles(sceneKey) {
  try {
    const raw = localStorage.getItem(candlesKey(sceneKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch (_) { /* ignore */ }
  return null;
}

function saveStoredCandles(sceneKey, candles) {
  try { localStorage.setItem(candlesKey(sceneKey), JSON.stringify(candles)); } catch (_) { /* ignore */ }
}

function loadStoredPanelPos(sceneKey) {
  try {
    const raw = localStorage.getItem(panelPosKey(sceneKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.x === 'number' && typeof parsed.y === 'number') return parsed;
  } catch (_) { /* ignore */ }
  return null;
}

function pctToNumber(pctString) {
  return parseFloat(String(pctString).replace('%', '')) || 0;
}

function formatJsSnippet(candles) {
  const lines = candles.map((c) => {
    return `  { left: '${c.left}', top: '${c.top}', size: ${c.size}, glow: ${c.glow}, speed: ${c.speed}, phase: ${c.phase} },`;
  });
  return `export const DEFAULT_CANDLES = [\n${lines.join('\n')}\n];`;
}

export default class CandlePlacementDev extends Component {
  constructor(props) {
    super(props);
    const stored = loadStoredCandles(props.sceneKey);
    const storedPos = loadStoredPanelPos(props.sceneKey);
    this.state = {
      enabled: false,
      candles: stored || props.defaultCandles,
      selected: 0,
      dragging: null,
      copied: false,
      panelPos: storedPos || { x: window.innerWidth - 296, y: 100 },
      panelDragOffset: null,
    };
  }

  componentDidMount() {
    // If we hydrated positions from localStorage, publish them immediately
    // so the parent scene renders with the restored buffer, not the defaults.
    if (this.state.candles !== this.props.defaultCandles) {
      this.props.onChange?.(this.state.candles);
    }
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);
  }

  componentWillUnmount() {
    this._unmounted = true;
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
    if (this._rectTickRaf) cancelAnimationFrame(this._rectTickRaf);
  }

  updateCandles = (next) => {
    this.setState({ candles: next });
    saveStoredCandles(this.props.sceneKey, next);
    this.props.onChange?.(next);
  };

  // Resolve the canvas screen rect via callback so this dev tool can be
  // rendered OUTSIDE the canvas's containing-block trap (zoom + transform
  // both prevent fixed-positioning from anchoring to the viewport).
  // Falls back to the local catcher's rect when no callback is supplied.
  getCanvasRect = () => {
    if (this.props.getCanvasRect) {
      const r = this.props.getCanvasRect();
      if (r) return r;
    }
    return this.canvasRef?.getBoundingClientRect()
      || { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
  };

  pctFromEvent = (e) => {
    const rect = this.getCanvasRect();
    const leftPct = ((e.clientX - rect.left) / rect.width) * 100;
    const topPct = ((e.clientY - rect.top) / rect.height) * 100;
    return {
      left: leftPct.toFixed(1) + '%',
      top: topPct.toFixed(1) + '%',
    };
  };

  toggleEnabled = () => this.setState((s) => ({ enabled: !s.enabled }));

  handleCanvasClick = (e) => {
    // Only fires on the transparent full-screen catcher. Not on handles.
    const { left, top } = this.pctFromEvent(e);
    const next = [...this.state.candles, { left, top, size: 11, glow: 70, speed: 3.2, phase: Math.random() * 6 }];
    this.updateCandles(next);
    this.setState({ selected: next.length - 1 });
  };

  handleHandleMouseDown = (i, e) => {
    e.stopPropagation();
    if (e.button === 2) return; // right click → delete via contextmenu
    this.setState({ dragging: i, selected: i });
  };

  handleHandleContextMenu = (i, e) => {
    e.preventDefault();
    e.stopPropagation();
    const next = this.state.candles.filter((_, idx) => idx !== i);
    this.updateCandles(next);
    this.setState({ selected: Math.min(this.state.selected, next.length - 1) });
  };

  handleMouseMove = (e) => {
    const { dragging, panelDragOffset } = this.state;
    if (panelDragOffset != null) {
      const x = Math.max(0, Math.min(window.innerWidth - 280, e.clientX - panelDragOffset.x));
      const y = Math.max(0, Math.min(window.innerHeight - 60, e.clientY - panelDragOffset.y));
      this.setState({ panelPos: { x, y } });
      return;
    }
    if (dragging == null) return;
    const { left, top } = this.pctFromEvent(e);
    const next = this.state.candles.map((c, i) => (i === dragging ? { ...c, left, top } : c));
    this.updateCandles(next);
  };

  handleMouseUp = () => {
    if (this.state.panelDragOffset != null) {
      try { localStorage.setItem(panelPosKey(this.props.sceneKey), JSON.stringify(this.state.panelPos)); } catch (_) { /* ignore */ }
      this.setState({ panelDragOffset: null });
    }
    if (this.state.dragging != null) this.setState({ dragging: null });
  };

  handlePanelDragStart = (e) => {
    e.stopPropagation();
    this.setState({
      panelDragOffset: { x: e.clientX - this.state.panelPos.x, y: e.clientY - this.state.panelPos.y },
    });
  };

  updateField = (i, key, value) => {
    const next = this.state.candles.map((c, idx) => (idx === i ? { ...c, [key]: value } : c));
    this.updateCandles(next);
  };

  copyToClipboard = async () => {
    const snippet = formatJsSnippet(this.state.candles);
    try {
      await navigator.clipboard.writeText(snippet);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 1500);
    } catch (_) { /* ignore */ }
  };

  resetToDefault = () => {
    if (!window.confirm('Reset candle positions to the in-code defaults?')) return;
    this.updateCandles(this.props.defaultCandles);
    this.setState({ selected: 0 });
  };

  clearAll = () => {
    if (!window.confirm('Remove all candles and start from scratch?')) return;
    this.updateCandles([]);
    this.setState({ selected: 0 });
  };

  componentDidUpdate() {
    // Force a re-render on the next frame whenever edit mode is on so
    // the catcher overlay tracks the canvas rect even if the canvas
    // scrolls or resizes. Cheap because nothing else in this component
    // re-renders without state changes.
    if (this.state.enabled && this.props.getCanvasRect && !this._rectTickRaf) {
      this._rectTickRaf = requestAnimationFrame(() => {
        this._rectTickRaf = null;
        if (this._unmounted) return;
        this.forceUpdate();
      });
    }
  }

  render() {
    const { enabled, candles, selected, copied, panelPos, panelDragOffset } = this.state;
    // When a parent ref is supplied, project the catcher overlay onto
    // the canvas's screen rect using viewport-fixed coordinates. The
    // dev tool itself is rendered outside the canvas, so `position:
    // fixed` here genuinely means "fixed to the viewport".
    const canvasRect = enabled && this.props.getCanvasRect ? this.props.getCanvasRect() : null;

    return (
      <>
        <button
          type="button"
          onClick={this.toggleEnabled}
          className="fixed z-[200] font-mono text-[0.6875cqw] px-3 py-1.5 rounded"
          style={{
            top: 64,
            right: 16,
            ...(this.props.togglePosition || {}),
            background: enabled ? 'rgba(180,60,60,0.85)' : 'rgba(30,20,12,0.85)',
            border: `1px solid ${enabled ? 'rgba(255,120,120,0.8)' : 'rgba(212,168,67,0.6)'}`,
            color: enabled ? '#fff1e0' : '#e8d5a0',
            cursor: 'pointer',
            userSelect: 'none',
            // Override the canvas's pointer-events: none so the pill is
            // actually clickable when this dev tool is nested inside it.
            pointerEvents: 'auto',
          }}
        >
          {enabled ? `✕ Exit edit (${this.props.sceneKey})` : `🕯 Edit candles (${this.props.sceneKey})`}
        </button>

        {enabled ? (
          <>
            {/* Click-catcher: when a canvas rect is supplied, project the
                catcher onto the canvas via fixed viewport coordinates so
                the dev tool can be rendered outside the canvas (escaping
                its zoom/transform containing block). Otherwise fall back
                to filling the parent (legacy nested usage). */}
            <div
              ref={(el) => { this.canvasRef = el; }}
              onClick={this.handleCanvasClick}
              onContextMenu={(e) => e.preventDefault()}
              className={canvasRect ? 'z-[150]' : 'absolute inset-0 z-[150]'}
              style={canvasRect ? {
                position: 'fixed',
                left: canvasRect.left,
                top: canvasRect.top,
                width: canvasRect.width,
                height: canvasRect.height,
                cursor: 'crosshair',
                background: 'rgba(0,0,0,0.15)',
                pointerEvents: 'auto',
              } : {
                cursor: 'crosshair',
                background: 'rgba(0,0,0,0.15)',
                pointerEvents: 'auto',
              }}
            />

            {/* Draggable handles. Same dual-mode positioning. When a
                canvas rect is supplied, % values are resolved against
                that rect into absolute viewport pixels. */}
            {candles.map((c, i) => {
              const isSel = i === selected;
              const handleStyle = canvasRect ? {
                position: 'fixed',
                left: canvasRect.left + (parseFloat(c.left) / 100) * canvasRect.width,
                top: canvasRect.top + (parseFloat(c.top) / 100) * canvasRect.height,
              } : {
                position: 'absolute',
                left: c.left,
                top: c.top,
              };
              return (
                <div
                  key={i}
                  onMouseDown={(e) => this.handleHandleMouseDown(i, e)}
                  onContextMenu={(e) => this.handleHandleContextMenu(i, e)}
                  className="z-[160] flex items-center justify-center font-mono text-[0.625cqw] font-bold"
                  style={{
                    ...handleStyle,
                    transform: 'translate(-50%, -50%)',
                    width: '1.375cqw',
                    height: '1.375cqw',
                    borderRadius: '50%',
                    background: isSel ? 'rgba(212,168,67,0.95)' : 'rgba(180,60,60,0.85)',
                    border: `2px solid ${isSel ? '#fff' : '#ffcfcf'}`,
                    color: '#000',
                    cursor: 'grab',
                    userSelect: 'none',
                    boxShadow: '0 0 8px rgba(0,0,0,0.6)',
                    pointerEvents: 'auto',
                  }}
                >
                  {i}
                </div>
              );
            })}

            {/* Side panel */}
            <div
              className="fixed z-[170] flex flex-col font-mono text-[0.6875cqw]"
              style={{
                left: panelPos.x,
                top: panelPos.y,
                width: '17.5cqw',
                maxHeight: '70vh',
                background: 'rgba(12,10,8,0.95)',
                border: '1px solid rgba(212,168,67,0.4)',
                borderRadius: 'var(--radius-md)',
                color: '#e8d5a0',
                padding: 10,
                boxShadow: panelDragOffset != null ? '0 8px 32px rgba(0,0,0,0.8)' : '0 4px 20px rgba(0,0,0,0.6)',
                backdropFilter: 'blur(4px)',
                userSelect: panelDragOffset != null ? 'none' : 'auto',
                pointerEvents: 'auto',
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="flex items-center justify-between mb-2"
                onMouseDown={this.handlePanelDragStart}
                style={{ cursor: panelDragOffset != null ? 'grabbing' : 'grab', paddingBottom: 4, borderBottom: '1px solid rgba(212,168,67,0.15)' }}
              >
                <span className="font-bold text-[0.625cqw] flex items-center gap-1.5" style={{ color: 'rgba(212,168,67,0.8)' }}>
                  <span style={{ letterSpacing: 2, opacity: 0.6 }}>⋮⋮</span>
                  Candles ({candles.length})
                </span>
                <div className="flex gap-1" onMouseDown={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    onClick={this.copyToClipboard}
                    className="px-2 py-1 rounded"
                    style={{ background: copied ? 'rgba(60,160,90,0.85)' : 'rgba(60,80,120,0.85)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', cursor: 'pointer' }}
                  >
                    {copied ? '✓ Copied' : 'Copy JS'}
                  </button>
                  <button
                    type="button"
                    onClick={this.clearAll}
                    className="px-2 py-1 rounded"
                    style={{ background: 'rgba(60,60,60,0.85)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', cursor: 'pointer' }}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={this.resetToDefault}
                    className="px-2 py-1 rounded"
                    style={{ background: 'rgba(80,40,40,0.85)', border: '1px solid rgba(255,120,120,0.4)', color: '#fff', cursor: 'pointer' }}
                  >
                    Reset
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto" style={{ flex: 1 }}>
                {candles.map((c, i) => (
                  <div
                    key={i}
                    className="mb-2 p-2 rounded cursor-pointer"
                    style={{
                      background: i === selected ? 'rgba(212,168,67,0.15)' : 'rgba(0,0,0,0.4)',
                      border: i === selected ? '1px solid rgba(212,168,67,0.5)' : '1px solid rgba(255,255,255,0.06)',
                    }}
                    onClick={() => this.setState({ selected: i })}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold">#{i}</span>
                      <span style={{ color: 'rgba(166,160,155,0.7)' }}>{c.left} · {c.top}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      {[
                        ['size', c.size, 1, 30],
                        ['glow', c.glow, 10, 200],
                        ['speed', c.speed, 0.5, 8],
                        ['phase', c.phase, 0, 6.3],
                      ].map(([key, val, min, max]) => (
                        <label key={key} className="flex items-center gap-1">
                          <span className="w-10" style={{ color: 'rgba(166,160,155,0.7)' }}>{key}</span>
                          <input
                            type="number"
                            min={min}
                            max={max}
                            step={key === 'speed' || key === 'phase' ? 0.1 : 1}
                            value={val}
                            onInput={(e) => this.updateField(i, key, parseFloat(e.target.value) || 0)}
                            onClick={(e) => e.stopPropagation()}
                            className="flex-1 px-1 py-0.5 min-w-0"
                            style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: '#e8d5a0', borderRadius: 'var(--radius-sm)' }}
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-2 text-[0.5625cqw]" style={{ color: 'rgba(166,160,155,0.55)' }}>
                Click empty space to add · drag to move · right-click handle to delete
              </div>
            </div>
          </>
        ) : null}
      </>
    );
  }
}
